// src/components/GlobalHeader.tsx
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { 
  FiUserCheck, 
  FiSettings, 
  FiCalendar, 
  FiList,
  FiRefreshCw,
  FiChevronLeft,
  FiChevronRight,
  FiPlus,
  FiUser,
  FiBriefcase,
  FiLogOut,
  FiBell
} from "react-icons/fi";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { supabase } from "../../lib/supabase";
import type { Project, Company } from "../../types/domain";
import NotificationPanel, { useNotifications } from "../NotificationPanel";
import "../GlobalHeader.css";

interface GlobalHeaderProps {
  // Información de contexto
  selectedProjectId?: string;
  selectedCompanyId?: string;
  projects?: Project[];
  companies?: Company[];
  
  // Tipo de vista actual
  viewType: 'kanban' | 'calendar' | 'gantt' | 'list' | 'dashboard';
  
  // Callbacks para acciones específicas de cada vista
  onSettingsClick?: () => void;
  onRefreshClick?: () => void;
  onUserClick?: () => void;
  onCreateTask?: () => void;
  
  // Props específicos del calendario
  currentView?: string;
  onViewChange?: (view: string) => void;
  
  // Props específicos del Gantt
  currentDate?: Date;
  onDateChange?: (date: Date) => void;
  
  // Props adicionales para diferentes vistas
  rightControls?: React.ReactNode;
}

export default function GlobalHeader({
  selectedProjectId,
  selectedCompanyId,
  projects = [],
  companies = [],
  viewType,
  onSettingsClick,
  onRefreshClick,
  onUserClick,
  onCreateTask,
  currentView,
  onViewChange,
  currentDate,
  onDateChange,
  rightControls
}: GlobalHeaderProps) {
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const { unreadCount, refreshUnreadCount } = useNotifications();

  // Close menu when clicking outside
  useEffect(() => {
    if (!showUserMenu) return;
    const handleClickOutside = () => setShowUserMenu(false);
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showUserMenu]);

  // Logout functionality
  const handleLogout = async () => {
    await supabase.auth.signOut();
    window.location.reload();
  };

  // Obtener el título dinámico
  const getTitle = () => {
    if (selectedCompanyId || selectedProjectId) {
      const companyName = selectedCompanyId ? companies.find(c => c.id === selectedCompanyId)?.name : '';
      const projectName = selectedProjectId ? projects.find(p => p.id === selectedProjectId)?.name : '';
      
      return (
        <>
          {companyName}
          {projectName && companyName ? ' / ' : ''}
          {projectName}
        </>
      );
    }
    return 'TaskManager';
  };

  // Renderizar controles específicos de cada vista
  const renderViewSpecificControls = () => {
    switch (viewType) {
      case 'calendar':
        return currentView && onViewChange ? (
          <div className="view-controls">
            <button 
              className={`view-btn ${currentView === 'month' ? 'active' : ''}`}
              onClick={() => onViewChange('month')}
            >
              <FiCalendar size={16} />
              Mes
            </button>
            <button 
              className={`view-btn ${currentView === 'week' ? 'active' : ''}`}
              onClick={() => onViewChange('week')}
            >
              <FiList size={16} />
              Semana
            </button>
            <button 
              className={`view-btn ${currentView === 'day' ? 'active' : ''}`}
              onClick={() => onViewChange('day')}
            >
              <FiList size={16} />
              Día
            </button>
          </div>
        ) : null;
        
      case 'kanban':
        return onSettingsClick ? (
          <button
            className="header-btn secondary"
            onClick={onSettingsClick}
          >
            <FiSettings size={16} />
            Columnas
          </button>
        ) : null;
        
      case 'gantt':
        return (
          <div className="gantt-controls">
            {currentDate && onDateChange && (
              <div className="month-navigator">
                <button
                  className="nav-btn"
                  onClick={() => onDateChange(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1))}
                >
                  <FiChevronLeft size={16} />
                </button>
                <span className="current-month">
                  {format(currentDate, 'MMMM yyyy', { locale: es })}
                </span>
                <button
                  className="nav-btn"
                  onClick={() => onDateChange(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1))}
                >
                  <FiChevronRight size={16} />
                </button>
              </div>
            )}
            {onRefreshClick && (
              <button
                className="header-btn secondary"
                onClick={onRefreshClick}
              >
                <FiRefreshCw size={16} />
              </button>
            )}
          </div>
        );
        
      default:
        return null;
    }
  };

  return (
    <header className="global-header">
      <div className="header-left">
        <h1 className="app-title">
          {getTitle()}
        </h1>
      </div>
      
      <div className="header-right">
        {/* Controles específicos de la vista */}
        {renderViewSpecificControls()}
        
        {/* Botón crear tarea (no mostrar en kanban que ya tiene sus propios + buttons) */}
        {viewType !== 'kanban' && onCreateTask && (
          <button 
            className="header-btn secondary"
            onClick={onCreateTask}
          >
            <FiPlus size={16} />
            Crear Tarea
          </button>
        )}
        
        {/* Controles personalizados adicionales */}
        {rightControls}
        
        {/* Botón de notificaciones */}
        <div className="notifications-container">
          <button 
            className="header-btn notification-btn"
            onClick={() => setShowNotifications(!showNotifications)}
            title="Notificaciones"
          >
            <FiBell size={18} />
            {unreadCount > 0 && (
              <span className="notification-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
            )}
          </button>
        </div>
        
        {/* Botón de usuario universal con menú */}
        <div className="user-menu-container">
          <button 
            className="header-btn user-btn"
            onClick={(e) => {
              e.stopPropagation();
              setShowUserMenu(!showUserMenu);
            }}
          >
            <FiUserCheck size={18} />
          </button>
          
          {showUserMenu && (
            <div className="user-menu">
              <Link to="/profile" className="menu-item" onClick={() => setShowUserMenu(false)}>
                <FiUser size={16} />
                <span>Editar perfil</span>
              </Link>
              <Link to="/companies" className="menu-item" onClick={() => setShowUserMenu(false)}>
                <FiBriefcase size={16} />
                <span>Empresas</span>
              </Link>
              <button 
                className="menu-item logout"
                onClick={handleLogout}
              >
                <FiLogOut size={16} />
                <span>Cerrar sesión</span>
              </button>
            </div>
          )}
        </div>
      </div>
      
      {/* Panel de notificaciones */}
      <NotificationPanel
        isOpen={showNotifications}
        onClose={() => {
          setShowNotifications(false);
          refreshUnreadCount();
        }}
      />
    </header>
  );
}